import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, ArrowLeftRight, Loader2, Minus, Plus, X } from 'lucide-react';
import {
  Sheet, SheetContent, SheetClose, SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import {
  vitalsService,
  RecordVitalsPayload,
  VitalLoinc,
  readVitalNumber,
  BpPosition,
  TempSite,
} from '@/services/vitalsService';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';
import {
  getInterpretation,
  interpretationLabel,
  isAbnormal,
  isCritical,
  Interpretation,
  bmiCategory,
  bmiCategoryColor,
} from '@/lib/vitals/ranges';
import { computeBmi } from '@/lib/vitals/units';
import {
  parseHeightString,
  inchesToFeetInches,
  feetInchesToInches,
} from '@/lib/vitals/parseHeight';
import PainScaleSegmented from './vitals/PainScaleSegmented';
import AbnormalSummary, { AbnormalEntry } from './vitals/AbnormalSummary';

interface VitalsCaptureSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  /**
   * When provided, vitals are written to the encounter-scoped endpoint.
   * When omitted, vitals are written via `recordPatientVitals` with
   * `off_encounter_reason='chart_correction'` — used by PatientVitalsTab
   * outside an active visit.
   */
  encounterId?: string;
  patientId: string;
  onRecorded?: () => void;
}

interface VitalsForm {
  bloodPressureSystolic: string;
  bloodPressureDiastolic: string;
  heartRate: string;
  respiratoryRate: string;
  temperature: string;
  temperatureUnit: 'F' | 'C';
  oxygenSaturation: string;
  weight: string;
  weightUnit: 'lb' | 'kg';
  // height is held two ways: dual ft+in fields when unit=in, single
  // value when unit=cm. We persist via heightInches/heightCm strings
  // and reconcile on save.
  heightFt: string;
  heightIn: string;
  heightCm: string;
  heightUnit: 'in' | 'cm';
  painScale: number | null;
  note: string;
  effectiveDateTime: string;
  bpPosition: BpPosition | '';
  tempSite: TempSite | '';
}

const EMPTY: VitalsForm = {
  bloodPressureSystolic: '',
  bloodPressureDiastolic: '',
  heartRate: '',
  respiratoryRate: '',
  temperature: '',
  temperatureUnit: 'F',
  oxygenSaturation: '',
  weight: '',
  weightUnit: 'lb',
  heightFt: '',
  heightIn: '',
  heightCm: '',
  heightUnit: 'in',
  painScale: null,
  note: '',
  effectiveDateTime: '',
  bpPosition: '',
  tempSite: '',
};

const NORMAL_DEFAULTS = {
  bloodPressureSystolic: '118',
  bloodPressureDiastolic: '76',
  heartRate: '72',
  respiratoryRate: '16',
  temperature: '98.6',
  oxygenSaturation: '98',
};

const toNumber = (s: string): number | undefined => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
};

// HTML datetime-local needs a "YYYY-MM-DDTHH:mm" string in local time.
const toDateTimeLocal = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatPriorDate = (s?: string | null): string => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const interpretationPillClass = (i: Interpretation): string => {
  if (i === 'LL' || i === 'HH') return 'bg-red-100 text-red-800 border-red-200';
  if (i === 'L' || i === 'H') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const adjustNumeric = (current: string, delta: number): string => {
  const base = toNumber(current) ?? 0;
  const next = base + delta;
  // Sub-integer steps (e.g. 0.1 for temperature) need decimal precision —
  // raw float math gives 98.6+0.1=98.69999…
  const decimals = Math.abs(delta) < 1 ? 1 : 0;
  return next.toFixed(decimals);
};

interface VitalNumberFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  loinc?: string;
  unitLabel?: string;
  step?: string;
  /**
   * Increment used by the +/- buttons. Falls back to `step` parsed as a
   * float, then 1. Kept separate from the HTML `step` attribute so the
   * native spinners and explicit buttons can diverge if needed.
   */
  stepAmount?: number;
  placeholder?: string;
  prior?: string;
  /**
   * When provided, the "Last: …" hint becomes a clickable chip that
   * copies the prior value into the field. Caller owns the actual
   * fill logic (some priors fill multiple fields, e.g. BP).
   */
  onApplyPrior?: () => void;
  disabled?: boolean;
}

const VitalNumberField: React.FC<VitalNumberFieldProps> = ({
  id, label, value, onChange, loinc, unitLabel, step, stepAmount,
  placeholder, prior, onApplyPrior, disabled,
}) => {
  const num = toNumber(value);
  const interp = loinc ? getInterpretation(num ?? null, loinc) : null;
  const parsedStep = step ? parseFloat(step) : NaN;
  const delta = stepAmount ?? (Number.isFinite(parsedStep) ? parsedStep : 1);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-stretch">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(adjustNumeric(value, -delta))}
          disabled={disabled}
          className="flex items-center justify-center w-8 h-10 rounded-l-md border border-r-0 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <Input
          id={id}
          type="number"
          step={step}
          placeholder={placeholder ?? unitLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="rounded-none text-center"
        />
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(adjustNumeric(value, delta))}
          disabled={disabled}
          className="flex items-center justify-center w-8 h-10 rounded-r-md border border-l-0 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between min-h-[18px] text-xs">
        {prior ? (
          onApplyPrior ? (
            <button
              type="button"
              onClick={onApplyPrior}
              disabled={disabled}
              title="Use this value"
              className="text-slate-500 hover:text-brand-teal hover:underline disabled:opacity-50 disabled:no-underline"
            >
              Last: {prior}
            </button>
          ) : (
            <span className="text-slate-500">Last: {prior}</span>
          )
        ) : <span />}
        {interp && interp !== 'N' && (
          <span className={`px-1.5 py-0.5 rounded border ${interpretationPillClass(interp)}`}>
            {interpretationLabel(interp)}
          </span>
        )}
      </div>
    </div>
  );
};

const VitalsCaptureSheet = ({
  open, onOpenChange, clinicId, encounterId, patientId, onRecorded,
}: VitalsCaptureSheetProps) => {
  const qc = useQueryClient();
  const [form, setForm] = useState<VitalsForm>(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const [criticalConfirmOpen, setCriticalConfirmOpen] = useState(false);
  const [priorVitals, setPriorVitals] = useState<Record<string, number | null>>({});
  const [priorDates, setPriorDates] = useState<Record<string, string | null>>({});

  // Reset + load priors when the sheet opens.
  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, effectiveDateTime: toDateTimeLocal(new Date()) });
    let cancelled = false;
    (async () => {
      try {
        const latest = await vitalsService.getLatestVitals(clinicId, patientId);
        if (cancelled) return;
        const codes = [
          VitalLoinc.SYSTOLIC_BP,
          VitalLoinc.DIASTOLIC_BP,
          VitalLoinc.HEART_RATE,
          VitalLoinc.RESPIRATORY_RATE,
          VitalLoinc.BODY_TEMPERATURE,
          VitalLoinc.OXYGEN_SATURATION,
          VitalLoinc.BODY_WEIGHT,
          VitalLoinc.BODY_HEIGHT,
        ];
        const values: Record<string, number | null> = {};
        const dates: Record<string, string | null> = {};
        for (const code of codes) {
          const obs = latest[code];
          values[code] = obs ? readVitalNumber(obs) : null;
          dates[code] = obs?.effective_date_time ?? null;
        }
        setPriorVitals(values);
        setPriorDates(dates);
      } catch {
        // Silent — prior-vitals hints are a nice-to-have, not required.
      }
    })();
    return () => { cancelled = true; };
  }, [open, clinicId, patientId]);

  const update = <K extends keyof VitalsForm>(key: K, value: VitalsForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // ------------------------------------------------------------------
  // Derived: numeric height in current unit (single source of truth)
  // ------------------------------------------------------------------
  const heightInCurrentUnit = useMemo(() => {
    if (form.heightUnit === 'in') {
      return feetInchesToInches(toNumber(form.heightFt) ?? null, toNumber(form.heightIn) ?? null);
    }
    return toNumber(form.heightCm) ?? null;
  }, [form.heightUnit, form.heightFt, form.heightIn, form.heightCm]);

  const heightInches = useMemo(() => {
    if (heightInCurrentUnit == null) return null;
    return form.heightUnit === 'cm' ? heightInCurrentUnit / 2.54 : heightInCurrentUnit;
  }, [heightInCurrentUnit, form.heightUnit]);

  // ------------------------------------------------------------------
  // BMI live preview
  // ------------------------------------------------------------------
  const bmi = useMemo(() => {
    const w = toNumber(form.weight);
    if (w == null || heightInCurrentUnit == null) return null;
    return computeBmi(w, form.weightUnit, heightInCurrentUnit, form.heightUnit);
  }, [form.weight, form.weightUnit, form.heightUnit, heightInCurrentUnit]);

  const bmiCat = bmi != null ? bmiCategory(bmi) : null;

  // ------------------------------------------------------------------
  // Smart paste handler on the inches field — accepts "5'10\"" syntax.
  // ------------------------------------------------------------------
  const handleInchesBlur = (raw: string) => {
    const parsed = parseHeightString(raw);
    if (parsed == null) return;
    const fi = inchesToFeetInches(parsed);
    if (!fi) return;
    setForm(prev => ({
      ...prev,
      heightFt: String(fi.ft),
      heightIn: fi.in === 0 ? '' : String(fi.in),
    }));
  };

  // ------------------------------------------------------------------
  // BP swap detection
  // ------------------------------------------------------------------
  const sys = toNumber(form.bloodPressureSystolic);
  const dia = toNumber(form.bloodPressureDiastolic);
  const bpReversed = sys != null && dia != null && sys < dia;

  const swapBp = () => setForm(prev => ({
    ...prev,
    bloodPressureSystolic: prev.bloodPressureDiastolic,
    bloodPressureDiastolic: prev.bloodPressureSystolic,
  }));

  // ------------------------------------------------------------------
  // Abnormal summary entries (also used to gate the save flow)
  // ------------------------------------------------------------------
  const abnormalEntries: AbnormalEntry[] = useMemo(() => {
    const entries: AbnormalEntry[] = [];
    const push = (label: string, value: number | undefined, loinc: string, unit: string) => {
      const i = getInterpretation(value ?? null, loinc);
      if (i && isAbnormal(i)) entries.push({ label, valueText: `${value} ${unit}`, interpretation: i });
    };
    push('Systolic BP', sys, VitalLoinc.SYSTOLIC_BP, 'mmHg');
    push('Diastolic BP', dia, VitalLoinc.DIASTOLIC_BP, 'mmHg');
    push('Heart rate', toNumber(form.heartRate), VitalLoinc.HEART_RATE, 'bpm');
    push('Respiratory rate', toNumber(form.respiratoryRate), VitalLoinc.RESPIRATORY_RATE, '/min');
    push('Temperature', toNumber(form.temperature), VitalLoinc.BODY_TEMPERATURE, `°${form.temperatureUnit}`);
    push('SpO₂', toNumber(form.oxygenSaturation), VitalLoinc.OXYGEN_SATURATION, '%');
    if (bmi != null) push('BMI', bmi, VitalLoinc.BMI, 'kg/m²');
    return entries;
  }, [sys, dia, form.heartRate, form.respiratoryRate, form.temperature, form.temperatureUnit, form.oxygenSaturation, bmi]);

  const hasCritical = abnormalEntries.some(e => isCritical(e.interpretation));

  const fillNormalDefaults = () => {
    setForm(prev => ({
      ...prev,
      ...NORMAL_DEFAULTS,
      temperatureUnit: 'F',
    }));
  };

  // ------------------------------------------------------------------
  // Prior-value fill helpers (per-field click + bulk "Use previous")
  // ------------------------------------------------------------------
  const hasAnyPriors = useMemo(
    () => Object.values(priorVitals).some(v => v != null),
    [priorVitals],
  );

  const applyPriorBp = () => {
    const s = priorVitals[VitalLoinc.SYSTOLIC_BP];
    const d = priorVitals[VitalLoinc.DIASTOLIC_BP];
    if (s == null && d == null) return;
    setForm(prev => ({
      ...prev,
      bloodPressureSystolic: s != null ? String(s) : prev.bloodPressureSystolic,
      bloodPressureDiastolic: d != null ? String(d) : prev.bloodPressureDiastolic,
    }));
  };

  const applyPriorSingle = (loinc: string, key: keyof VitalsForm) => () => {
    const v = priorVitals[loinc];
    if (v == null) return;
    setForm(prev => ({ ...prev, [key]: String(v) } as VitalsForm));
  };

  const applyPriorHeight = () => {
    const ht = priorVitals[VitalLoinc.BODY_HEIGHT];
    if (ht == null) return;
    setForm(prev => {
      if (prev.heightUnit === 'in') {
        const fi = inchesToFeetInches(ht);
        if (!fi) return prev;
        return {
          ...prev,
          heightFt: String(fi.ft),
          heightIn: fi.in === 0 ? '' : String(fi.in),
        };
      }
      return { ...prev, heightCm: String(ht) };
    });
  };

  const applyPreviousAll = () => {
    setForm(prev => {
      const next: VitalsForm = { ...prev };
      const set = (key: keyof VitalsForm, loinc: string) => {
        const v = priorVitals[loinc];
        if (v != null) (next as Record<string, unknown>)[key as string] = String(v);
      };
      set('bloodPressureSystolic', VitalLoinc.SYSTOLIC_BP);
      set('bloodPressureDiastolic', VitalLoinc.DIASTOLIC_BP);
      set('heartRate', VitalLoinc.HEART_RATE);
      set('respiratoryRate', VitalLoinc.RESPIRATORY_RATE);
      set('temperature', VitalLoinc.BODY_TEMPERATURE);
      set('oxygenSaturation', VitalLoinc.OXYGEN_SATURATION);
      set('weight', VitalLoinc.BODY_WEIGHT);

      const ht = priorVitals[VitalLoinc.BODY_HEIGHT];
      if (ht != null) {
        if (next.heightUnit === 'in') {
          const fi = inchesToFeetInches(ht);
          if (fi) {
            next.heightFt = String(fi.ft);
            next.heightIn = fi.in === 0 ? '' : String(fi.in);
          }
        } else {
          next.heightCm = String(ht);
        }
      }
      return next;
    });
  };

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------
  const buildPayload = (): RecordVitalsPayload | null => {
    const anyEntered = [
      form.bloodPressureSystolic, form.bloodPressureDiastolic,
      form.heartRate, form.respiratoryRate, form.temperature,
      form.oxygenSaturation, form.weight,
    ].some((v) => v.trim() !== '') || heightInCurrentUnit != null || form.painScale != null;
    if (!anyEntered) {
      toast({
        title: 'No values entered',
        description: 'Fill at least one vital to save.',
        variant: 'destructive',
      });
      return null;
    }

    let effectiveDateTime: string;
    try {
      effectiveDateTime = form.effectiveDateTime
        ? new Date(form.effectiveDateTime).toISOString()
        : new Date().toISOString();
    } catch {
      effectiveDateTime = new Date().toISOString();
    }

    return {
      patientId,
      bloodPressureSystolic: toNumber(form.bloodPressureSystolic),
      bloodPressureDiastolic: toNumber(form.bloodPressureDiastolic),
      heartRate: toNumber(form.heartRate),
      respiratoryRate: toNumber(form.respiratoryRate),
      temperature: toNumber(form.temperature),
      temperatureUnit: form.temperatureUnit,
      oxygenSaturation: toNumber(form.oxygenSaturation),
      weight: toNumber(form.weight),
      weightUnit: form.weightUnit,
      height: heightInCurrentUnit ?? undefined,
      heightUnit: form.heightUnit,
      painScale: form.painScale ?? undefined,
      note: form.note.trim() || undefined,
      effectiveDateTime,
      bpPosition: form.bpPosition || undefined,
      tempSite: form.tempSite || undefined,
    };
  };

  const handleSaveClick = () => {
    if (hasCritical) {
      setCriticalConfirmOpen(true);
      return;
    }
    void persistPayload();
  };

  const persistPayload = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setIsSaving(true);
    try {
      const result = encounterId
        ? await vitalsService.recordVitals(clinicId, encounterId, payload)
        : await vitalsService.recordPatientVitals(clinicId, patientId, payload);
      toast({
        title: `Recorded ${result.count} vital${result.count === 1 ? '' : 's'}`,
        variant: 'success',
      });
      // Centralized invalidation so every caller (rooming panel, patient
      // vitals tab, intellihealth, etc.) sees fresh data without each
      // having to wire it up in their own onRecorded callback.
      qc.invalidateQueries({ queryKey: cacheRoots.patientVitals(patientId) });
      if (encounterId) {
        qc.invalidateQueries({ queryKey: cacheKeys.encounterVitals(encounterId) });
      }
      setForm(EMPTY);
      onRecorded?.();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Failed to save vitals',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
      setCriticalConfirmOpen(false);
    }
  };

  // ------------------------------------------------------------------
  // Prior-value display strings
  // ------------------------------------------------------------------
  const priorText = (loinc: string, unit: string): string | undefined => {
    const v = priorVitals[loinc];
    if (v == null) return undefined;
    const dateStr = formatPriorDate(priorDates[loinc]);
    return `${v} ${unit}${dateStr ? ` · ${dateStr}` : ''}`;
  };

  const priorBp = (() => {
    const s = priorVitals[VitalLoinc.SYSTOLIC_BP];
    const d = priorVitals[VitalLoinc.DIASTOLIC_BP];
    if (s == null || d == null) return undefined;
    const dateStr = formatPriorDate(priorDates[VitalLoinc.SYSTOLIC_BP]);
    return `${s}/${d} mmHg${dateStr ? ` · ${dateStr}` : ''}`;
  })();

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full !max-w-[560px] p-0 flex flex-col gap-0 [&>button]:hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-violet-500" />
              <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
                Capture Vitals
              </SheetTitle>
            </div>
            <SheetClose asChild>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </SheetClose>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Header strip: timestamp + Normal vitals quick-fill */}
            <div className="flex items-end gap-3">
              <div className="space-y-1.5 flex-1">
                <Label htmlFor="effective-at">Recorded at</Label>
                <Input
                  id="effective-at"
                  type="datetime-local"
                  value={form.effectiveDateTime}
                  onChange={(e) => update('effectiveDateTime', e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyPreviousAll}
                disabled={isSaving || !hasAnyPriors}
                title={hasAnyPriors ? 'Fill from most recent visit' : 'No prior vitals available'}
              >
                Use previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fillNormalDefaults}
                disabled={isSaving}
              >
                Normal vitals
              </Button>
            </div>

            {/* Cardiopulmonary */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cardiopulmonary</h3>

              {bpReversed && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 flex items-center justify-between">
                  <div className="text-sm text-amber-900">
                    BP looks reversed ({form.bloodPressureSystolic}/{form.bloodPressureDiastolic}). Systolic should be the larger number.
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={swapBp}>
                    <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                    Swap
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <VitalNumberField
                  id="bp-sys"
                  label="BP systolic"
                  value={form.bloodPressureSystolic}
                  onChange={(v) => update('bloodPressureSystolic', v)}
                  loinc={VitalLoinc.SYSTOLIC_BP}
                  unitLabel="mmHg"
                  prior={priorBp}
                  onApplyPrior={priorBp ? applyPriorBp : undefined}
                  disabled={isSaving}
                />
                <VitalNumberField
                  id="bp-dia"
                  label="BP diastolic"
                  value={form.bloodPressureDiastolic}
                  onChange={(v) => update('bloodPressureDiastolic', v)}
                  loinc={VitalLoinc.DIASTOLIC_BP}
                  unitLabel="mmHg"
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bp-position">BP position</Label>
                <Select
                  value={form.bpPosition || undefined}
                  onValueChange={(v) => update('bpPosition', v as BpPosition)}
                  disabled={isSaving}
                >
                  <SelectTrigger id="bp-position"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sitting">Sitting</SelectItem>
                    <SelectItem value="standing">Standing</SelectItem>
                    <SelectItem value="supine">Supine</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <VitalNumberField
                  id="hr"
                  label="Heart rate (bpm)"
                  value={form.heartRate}
                  onChange={(v) => update('heartRate', v)}
                  loinc={VitalLoinc.HEART_RATE}
                  unitLabel="bpm"
                  prior={priorText(VitalLoinc.HEART_RATE, 'bpm')}
                  onApplyPrior={applyPriorSingle(VitalLoinc.HEART_RATE, 'heartRate')}
                  disabled={isSaving}
                />
                <VitalNumberField
                  id="rr"
                  label="Respiratory rate"
                  value={form.respiratoryRate}
                  onChange={(v) => update('respiratoryRate', v)}
                  loinc={VitalLoinc.RESPIRATORY_RATE}
                  unitLabel="/min"
                  prior={priorText(VitalLoinc.RESPIRATORY_RATE, '/min')}
                  onApplyPrior={applyPriorSingle(VitalLoinc.RESPIRATORY_RATE, 'respiratoryRate')}
                  disabled={isSaving}
                />
              </div>
            </section>

            {/* Temperature & SpO₂ */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Temperature & SpO₂</h3>

              <div className="grid grid-cols-[1fr_90px_140px] gap-3">
                <VitalNumberField
                  id="temp"
                  label="Temperature"
                  value={form.temperature}
                  onChange={(v) => update('temperature', v)}
                  loinc={VitalLoinc.BODY_TEMPERATURE}
                  step="0.1"
                  stepAmount={0.1}
                  prior={priorText(VitalLoinc.BODY_TEMPERATURE, '°F')}
                  onApplyPrior={applyPriorSingle(VitalLoinc.BODY_TEMPERATURE, 'temperature')}
                  disabled={isSaving}
                />
                <div className="space-y-1.5">
                  <Label>Unit</Label>
                  <Select
                    value={form.temperatureUnit}
                    onValueChange={(v: 'F' | 'C') => update('temperatureUnit', v)}
                    disabled={isSaving}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="F">°F</SelectItem>
                      <SelectItem value="C">°C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Site</Label>
                  <Select
                    value={form.tempSite || undefined}
                    onValueChange={(v) => update('tempSite', v as TempSite)}
                    disabled={isSaving}
                  >
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oral">Oral</SelectItem>
                      <SelectItem value="tympanic">Tympanic</SelectItem>
                      <SelectItem value="temporal">Temporal</SelectItem>
                      <SelectItem value="axillary">Axillary</SelectItem>
                      <SelectItem value="rectal">Rectal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <VitalNumberField
                id="spo2"
                label="SpO₂ (%)"
                value={form.oxygenSaturation}
                onChange={(v) => update('oxygenSaturation', v)}
                loinc={VitalLoinc.OXYGEN_SATURATION}
                unitLabel="%"
                prior={priorText(VitalLoinc.OXYGEN_SATURATION, '%')}
                onApplyPrior={applyPriorSingle(VitalLoinc.OXYGEN_SATURATION, 'oxygenSaturation')}
                disabled={isSaving}
              />
            </section>

            {/* Anthropometrics */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Anthropometrics</h3>

              <div className="grid grid-cols-[1fr_90px] gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="wt">Weight</Label>
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      aria-label="Decrease weight"
                      onClick={() => update('weight', adjustNumeric(form.weight, -0.1))}
                      disabled={isSaving}
                      className="flex items-center justify-center w-8 h-10 rounded-l-md border border-r-0 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <Input
                      id="wt"
                      type="number"
                      step="0.1"
                      value={form.weight}
                      onChange={(e) => update('weight', e.target.value)}
                      disabled={isSaving}
                      className="rounded-none text-center"
                    />
                    <button
                      type="button"
                      aria-label="Increase weight"
                      onClick={() => update('weight', adjustNumeric(form.weight, 0.1))}
                      disabled={isSaving}
                      className="flex items-center justify-center w-8 h-10 rounded-r-md border border-l-0 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {priorText(VitalLoinc.BODY_WEIGHT, form.weightUnit) && (
                    <button
                      type="button"
                      onClick={applyPriorSingle(VitalLoinc.BODY_WEIGHT, 'weight')}
                      disabled={isSaving}
                      title="Use this value"
                      className="text-xs text-slate-500 hover:text-brand-teal hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      Last: {priorText(VitalLoinc.BODY_WEIGHT, form.weightUnit)}
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Unit</Label>
                  <Select
                    value={form.weightUnit}
                    onValueChange={(v: 'lb' | 'kg') => update('weightUnit', v)}
                    disabled={isSaving}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lb">lb</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.heightUnit === 'in' ? (
                <div className="grid grid-cols-[1fr_1fr_90px] gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ht-ft">Height (ft)</Label>
                    <Input
                      id="ht-ft"
                      type="number"
                      min="0"
                      max="8"
                      value={form.heightFt}
                      onChange={(e) => update('heightFt', e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ht-in">Height (in)</Label>
                    <Input
                      id="ht-in"
                      type="text"
                      inputMode="decimal"
                      placeholder='e.g. 10 or 5&apos;10"'
                      value={form.heightIn}
                      onChange={(e) => update('heightIn', e.target.value)}
                      onBlur={(e) => handleInchesBlur(e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Unit</Label>
                    <Select
                      value={form.heightUnit}
                      onValueChange={(v: 'in' | 'cm') => update('heightUnit', v)}
                      disabled={isSaving}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[1fr_90px] gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ht-cm">Height (cm)</Label>
                    <Input
                      id="ht-cm"
                      type="number"
                      step="0.1"
                      value={form.heightCm}
                      onChange={(e) => update('heightCm', e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Unit</Label>
                    <Select
                      value={form.heightUnit}
                      onValueChange={(v: 'in' | 'cm') => update('heightUnit', v)}
                      disabled={isSaving}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {bmi != null && bmiCat && (
                <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${bmiCategoryColor(bmiCat)}`}>
                  <span className="text-sm font-semibold">BMI {bmi.toFixed(1)}</span>
                  <span className="text-xs">·</span>
                  <span className="text-sm">{bmiCat}</span>
                </div>
              )}
            </section>

            {/* Pain & notes */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pain & notes</h3>

              <div className="space-y-1.5">
                <Label>Pain scale (0–10)</Label>
                <PainScaleSegmented
                  value={form.painScale}
                  onChange={(v) => update('painScale', v)}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="note">Notes</Label>
                <textarea
                  id="note"
                  className="w-full min-h-[80px] rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  value={form.note}
                  onChange={(e) => update('note', e.target.value)}
                  disabled={isSaving}
                />
              </div>
            </section>

            <AbnormalSummary entries={abnormalEntries} />
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={isSaving}
              className={`rounded-lg ${hasCritical ? 'bg-red-600 hover:bg-red-700' : ''}`}
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              ) : hasCritical ? 'Save anyway' : 'Save vitals'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={criticalConfirmOpen} onOpenChange={setCriticalConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm critical values</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>The following values are outside critical thresholds:</p>
                <ul className="space-y-0.5 text-sm pl-4 list-disc">
                  {abnormalEntries.filter(e => isCritical(e.interpretation)).map((e, i) => (
                    <li key={i}>
                      <span className="font-medium">{e.label}:</span> {e.valueText} ({interpretationLabel(e.interpretation).toLowerCase()})
                    </li>
                  ))}
                </ul>
                <p>Confirm these readings are accurate before saving.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void persistPayload()} disabled={isSaving}>
              Yes, I&apos;ve verified these values
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VitalsCaptureSheet;
